import { ChainId, Currency, CurrencyAmount, currencyEquals, ETHER, Price, Token, WETH9 } from '@xterswap/sdk-core'
import { useMemo } from 'react'
import { useV2TradeExactOut } from './useV2Trade'
import { useBestV3TradeExactOut, V3TradeState } from './useBestV3Trade'
import { useActiveWeb3React } from './web3'
import { USDT } from 'constants/tokens'

/**
 * Returns the price in USDC of the input currency
 * @param currency currency to compute the USDC price of
 */
export default function useUSDTPrice(currency?: Currency): Price<Currency, Token> | undefined {
  const { chainId } = useActiveWeb3React()

  const usdt = USDT[chainId as ChainId]
  const wbnb = WETH9[chainId as ChainId]

  // USDT amount used when calculating spot price for a given currency.
  // The amount is large enough to filter low liquidity pairs.
  const usdtCurrencyAmount = CurrencyAmount.fromRawAmount(usdt, 1e18)

  const v2USDTTrade = useV2TradeExactOut(
    currency && currencyEquals(currency, wbnb) ? ETHER : currency,
    usdtCurrencyAmount,
    {
      maxHops: 2,
    }
  )
  const v3USDTTrade = useBestV3TradeExactOut(
    currency && currencyEquals(currency, wbnb) ? ETHER : currency,
    usdtCurrencyAmount
  )

  return useMemo(() => {
    if (!currency || !chainId) {
      return undefined
    }

    if (currencyEquals(usdt, currency)) {
      return new Price(currency, usdt, 1, 1)
    }

    // use v2 price if available, v3 as fallback
    if (v2USDTTrade) {
      const { numerator, denominator } = v2USDTTrade.route.midPrice
      return new Price(currency, usdt, denominator, numerator)
    } else if (v3USDTTrade.state === V3TradeState.VALID && v3USDTTrade.trade) {
      const { numerator, denominator } = v3USDTTrade.trade.route.midPrice
      return new Price(currency, usdt, denominator, numerator)
    }

    return undefined
  }, [chainId, currency, usdt, v2USDTTrade, v3USDTTrade])
}

export function useUSDTValue(currencyAmount: CurrencyAmount<Currency> | undefined | null) {
  const price = useUSDTPrice(currencyAmount?.currency)

  return useMemo(() => {
    if (!price || !currencyAmount) return null
    try {
      return price.quote(currencyAmount)
    } catch (error) {
      return null
    }
  }, [currencyAmount, price])
}
